import { formatPhone } from '@fundline/shared/phone';
import { Link } from '@tanstack/react-router';
import { SquarePen } from 'lucide-react';
import { useState } from 'react';
import { Alert } from '../../components/ui/Alert.tsx';
import { Avatar } from '../../components/ui/Avatar.tsx';
import { Badge } from '../../components/ui/Badge.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { EmptyState, Spinner } from '../../components/ui/Layout.tsx';
import { useWorkspaceClaims } from '../../lib/auth.tsx';
import { DESKTOP_QUERY, useMediaQuery, WIDE_QUERY } from '../../lib/use-media-query.ts';
import { useCampaignLabels } from '../campaigns/use-campaigns.ts';
import { useMembersQuery } from '../members/use-members.ts';
import { ConversationView } from './ConversationView.tsx';
import { DetailsPanel } from './DetailsPanel.tsx';
import { formatRowTime } from './format.ts';
import { MessageStatusTicks } from './MessageStatus.tsx';
import { type InboxFilters, type InboxItem, type InboxView, useInboxQuery } from './use-inbox.ts';

/** Status views beside the segmented control (brand/layout.md "Conversation list", step 4). */
const STATUS_VIEWS: { value: InboxView; label: string }[] = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'problems', label: 'Problems' },
  { value: 'opted_out', label: 'Opted out' },
  { value: 'do_not_contact', label: 'Do not contact' },
];

const EMPTY: Record<InboxView, { title: string; body: string }> = {
  all: {
    title: 'No messages yet',
    body: 'Your next deal is one text away. Open a merchant and choose Text to start.',
  },
  unread: {
    title: 'Nothing unread',
    body: 'New texts from merchants show up here as they arrive.',
  },
  needs_reply: { title: 'Nothing needs a reply', body: "You're all caught up." },
  scheduled: {
    title: 'Nothing scheduled',
    body: "Texts waiting for a merchant's sending hours, or for something to be fixed, show up here.",
  },
  problems: {
    title: 'No problems',
    body: 'Texts that failed, were blocked or have an unknown delivery show up here.',
  },
  opted_out: {
    title: 'Nobody has opted out',
    body: 'Merchants who text STOP (or a similar word) show up here. Only they can resubscribe, by texting START.',
  },
  do_not_contact: {
    title: 'Nobody is marked do not contact',
    body: 'Merchants on hold for review, marked by your team or blocked at their carrier show up here.',
  },
};

const PROBLEM_LABEL: Record<string, string> = {
  failed: 'Failed',
  unknown: 'Delivery unknown',
  blocked: 'Not sent',
  no_number: 'No sending number',
};

export function merchantTitle(m: {
  businessName: string | null;
  ownerName: string | null;
  phone: string;
}): string {
  return m.businessName ?? m.ownerName ?? formatPhone(m.phone);
}

function Row({
  item,
  active,
  repName,
}: {
  item: InboxItem;
  active: boolean;
  repName: string | null;
}) {
  const named = item.businessName ?? item.ownerName;
  const unread = item.unreadCount > 0;
  return (
    <li>
      <Link
        to="/inbox/$merchantId"
        params={{ merchantId: item.merchantId }}
        search={(prev) => prev}
        className={`fl-row hover:bg-surface focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus-ring ${active ? 'fl-row--selected' : ''}`}
        data-testid="inbox-row"
        aria-current={active ? 'true' : undefined}
      >
        <span
          className={`fl-row__unread ${unread ? '' : 'fl-row__unread--none'}`}
          data-testid={unread ? 'unread-count' : undefined}
          aria-label={unread ? `${item.unreadCount} unread` : undefined}
        >
          {unread ? <span className="sr-only">{item.unreadCount}</span> : null}
        </span>
        <Avatar name={named} brand={active} />
        <span className="fl-row__body">
          <span className="fl-row__top">
            <span className="fl-row__name text-ink">{merchantTitle(item)}</span>
            <span className="fl-row__time">{formatRowTime(item.lastMessageAt)}</span>
          </span>
          <span className="fl-row__preview flex items-center gap-1" data-testid="inbox-preview">
            {item.lastDirection === 'out' && item.lastStatus ? (
              <MessageStatusTicks status={item.lastStatus} iconOnly />
            ) : null}
            <span className="truncate">
              {item.lastDirection === 'out' ? 'You: ' : ''}
              {item.preview}
            </span>
          </span>
          <span className="fl-row__tags flex-wrap items-center">
            {item.sendProblem ? (
              <Badge role="declined" testId="send-problem">
                {PROBLEM_LABEL[item.sendProblem] ?? item.sendProblem}
              </Badge>
            ) : null}
            {item.optedOut ? (
              <Badge role="declined" testId="opted-out">
                Opted out
              </Badge>
            ) : null}
            {item.doNotContact ? (
              <Badge role="declined" testId="do-not-contact">
                Do not contact
              </Badge>
            ) : null}
            {item.heldCount > 0 ? (
              <Badge role="pending" testId="scheduled">
                {item.heldCount > 1 ? `${item.heldCount} scheduled` : 'Scheduled'}
              </Badge>
            ) : null}
            {item.needsReply ? (
              <Badge role="pending" testId="needs-reply">
                Needs reply
              </Badge>
            ) : null}
            {repName ? <span className="footnote truncate text-ink-muted">{repName}</span> : null}
          </span>
        </span>
      </Link>
    </li>
  );
}

const SEGMENT =
  'fl-segmented__opt aria-pressed:bg-surface-raised aria-pressed:text-ink aria-pressed:shadow-card';

const CHIP =
  'footnote min-h-8 rounded-pill bg-surface px-3 font-semibold text-ink-muted ring-1 ring-hairline hover:text-ink focus-visible:outline-2 focus-visible:outline-focus-ring aria-pressed:bg-brand-tint aria-pressed:text-brand-on-tint aria-pressed:ring-0';

/** M9: the list shows one campaign's merchants (opened from the campaign's screen). */
function CampaignChip({ campaignId, onClear }: { campaignId: string; onClear: () => void }) {
  const { workspaceId } = useWorkspaceClaims();
  const names = useCampaignLabels(workspaceId, [campaignId]);
  return (
    <button
      type="button"
      aria-pressed
      onClick={onClear}
      className={CHIP}
      aria-label="Show every campaign"
      data-testid="campaign-filter"
    >
      Campaign: {names.data?.get(campaignId) ?? '…'} ×
    </button>
  );
}

function InboxList({
  filters,
  selectedId,
  onFiltersChange,
}: {
  filters: InboxFilters;
  selectedId: string | null;
  onFiltersChange: (next: InboxFilters) => void;
}) {
  const { workspaceId, role, userId } = useWorkspaceClaims();
  const inbox = useInboxQuery(workspaceId, filters);
  const members = useMembersQuery(workspaceId);
  const names = new Map((members.data ?? []).map((m) => [m.userId, m.fullName || m.email]));
  const items = inbox.data?.pages.flat() ?? [];
  const empty = EMPTY[filters.view];
  const admin = role === 'admin';
  // "Mine" is the admin's own merchants (reps only ever see theirs, so they don't get it).
  const mine = admin && filters.rep === userId;

  const segments: { label: string; pressed: boolean; next: InboxFilters }[] = [
    { label: 'All', pressed: filters.view === 'all' && !mine, next: { view: 'all', rep: null } },
    {
      label: 'Unread',
      pressed: filters.view === 'unread',
      next: { ...filters, view: 'unread' },
    },
    ...(admin
      ? [
          {
            label: 'Mine',
            pressed: mine && filters.view === 'all',
            next: { view: 'all' as const, rep: userId },
          },
        ]
      : []),
    {
      label: 'Needs reply',
      pressed: filters.view === 'needs_reply',
      next: { ...filters, view: 'needs_reply' },
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div role="group" aria-label="Show" className="fl-segmented self-start">
        {segments.map((s) => (
          <button
            key={s.label}
            type="button"
            aria-pressed={s.pressed}
            onClick={() => onFiltersChange(s.next)}
            className={SEGMENT}
          >
            {s.label}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 px-0">
        {STATUS_VIEWS.map((v) => (
          <button
            key={v.value}
            type="button"
            aria-pressed={filters.view === v.value}
            onClick={() =>
              onFiltersChange({ ...filters, view: filters.view === v.value ? 'all' : v.value })
            }
            className={CHIP}
          >
            {v.label}
          </button>
        ))}
        {admin ? (
          <select
            value={filters.rep ?? ''}
            onChange={(e) => onFiltersChange({ ...filters, rep: e.target.value || null })}
            className="footnote min-h-8 rounded-pill bg-surface px-3 font-semibold text-ink-muted ring-1 ring-hairline focus-visible:outline-2 focus-visible:outline-focus-ring"
            aria-label="Filter by rep"
          >
            <option value="">Every rep</option>
            {(members.data ?? [])
              .filter((m) => m.status === 'active')
              .map((m) => (
                <option key={m.userId} value={m.userId}>
                  {m.fullName || m.email}
                </option>
              ))}
          </select>
        ) : null}
        {filters.campaign ? (
          <CampaignChip
            campaignId={filters.campaign}
            onClear={() => onFiltersChange({ ...filters, campaign: null })}
          />
        ) : null}
      </div>

      {inbox.isPending ? (
        <Spinner label="Loading conversations" />
      ) : inbox.error ? (
        <Alert tone="error">
          {inbox.error.message}{' '}
          <button
            type="button"
            className="font-semibold underline"
            onClick={() => void inbox.refetch()}
          >
            Try again
          </button>
        </Alert>
      ) : items.length === 0 ? (
        <EmptyState title={empty.title}>{empty.body}</EmptyState>
      ) : (
        <ul className="fl-inbox -mx-4 md:-mx-6 split:-mx-3" aria-label="Conversations">
          {items.map((item) => (
            <Row
              key={item.merchantId}
              item={item}
              active={item.merchantId === selectedId}
              repName={admin ? (names.get(item.assignedRepId) ?? null) : null}
            />
          ))}
        </ul>
      )}
      {inbox.hasNextPage ? (
        <Button
          variant="secondary"
          busy={inbox.isFetchingNextPage}
          onClick={() => void inbox.fetchNextPage()}
        >
          Load more
        </Button>
      ) : null}
    </div>
  );
}

/**
 * The live inbox (brand/layout.md, breakpoints in ADR 0008). The conversation always keeps its 480px
 * minimum. From 912px the 360px list sits beside it; below that the list or the conversation shows on
 * its own with a back arrow (as on a phone). The 320px details panel is a column from 1392px, an
 * overlay below that and a bottom sheet on phones.
 */
export function InboxPage({
  filters,
  selectedId,
  onFiltersChange,
}: {
  filters: InboxFilters;
  selectedId: string | null;
  onFiltersChange: (next: InboxFilters) => void;
}) {
  const desktop = useMediaQuery(DESKTOP_QUERY);
  const wide = useMediaQuery(WIDE_QUERY);
  const [detailsChoice, setDetailsChoice] = useState<boolean | null>(null);
  // Open by default only where it is a column; a choice sticks while the inbox is open.
  const detailsOpen = detailsChoice ?? (desktop && wide);
  const detailsMode = !desktop ? 'sheet' : wide ? 'column' : 'overlay';

  return (
    <div className="flex min-h-0 min-w-0 flex-1">
      <section
        aria-label="Inbox"
        className={`w-full min-w-0 flex-col gap-4 px-4 pt-5 pb-24 md:overflow-y-auto md:px-6 md:pb-6 split:flex split:w-90 split:shrink-0 split:border-r split:border-hairline split:px-3 ${
          selectedId ? 'hidden' : 'flex'
        }`}
      >
        <header className="flex items-center justify-between gap-3">
          <h1 className="display-lg text-ink">Inbox</h1>
          <Link
            to="/merchants"
            className="fl-send size-11 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
            aria-label="New message"
            title="New message: pick a merchant"
          >
            <SquarePen size={20} strokeWidth={1.75} aria-hidden />
          </Link>
        </header>
        <InboxList filters={filters} selectedId={selectedId} onFiltersChange={onFiltersChange} />
      </section>

      {selectedId ? (
        <>
          <div className="flex min-h-0 min-w-0 flex-1 flex-col md:min-w-120">
            <ConversationView
              key={selectedId}
              merchantId={selectedId}
              detailsOpen={detailsOpen}
              onToggleDetails={() => setDetailsChoice(!detailsOpen)}
            />
          </div>
          {detailsOpen ? (
            <DetailsPanel
              key={selectedId}
              merchantId={selectedId}
              mode={detailsMode}
              onClose={() => setDetailsChoice(false)}
            />
          ) : null}
        </>
      ) : (
        <div className="hidden flex-1 items-center justify-center p-8 split:flex">
          <EmptyState title="Choose a conversation">
            Pick one on the left to read and reply.
          </EmptyState>
        </div>
      )}
    </div>
  );
}
